import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { Card } from 'react-bootstrap';
import '../users/userform.css';
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import axiosInstance from "../../services/AxiosInstance";
import { FiArrowLeft } from 'react-icons/fi';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import { FIELD_LIMITS } from '../../utils/fieldLimits';

const EditBranch = () => {
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [formData, setFormData] = useState({
    name: "",
    location: "",
    country: ""
  });
  const [branchData, setBranchData] = useState(null);
  const [dataLoaded, setDataLoaded] = useState(false);
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();

  // Helper function to safely navigate back, avoiding login page
  const navigateBackSafely = () => {
    navigate(-1);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value
    }));
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.name.trim()) newErrors.name = 'Branch name is required';
    else if (!/[a-zA-Z0-9]/.test(formData.name.trim())) newErrors.name = 'Branch name cannot contain only special characters.';
    else if (formData.name.length > FIELD_LIMITS.BRANCH_NAME) newErrors.name = `Branch name cannot exceed ${FIELD_LIMITS.BRANCH_NAME} characters`;
    if (!formData.country.trim()) newErrors.country = 'Country is required';
    if (formData.location.trim() && !/[a-zA-Z]/.test(formData.location.trim())) newErrors.location = 'Location must include letters and cannot be only numbers or special characters.';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    if (!validate()) return;

    setLoading(true);
    setIsSubmitting(true);

    const userData = JSON.parse(localStorage.getItem("user"));
    
    const payload = {
      name: formData.name,
      location: formData.location,
      country_id: formData.country,
      corporate_id: userData.corporateId,
      branch_id: id
    };
    
    try {
      const response = await axiosInstance.put(`/settings/updateBranch`, JSON.stringify(payload));

      if (!response) {
        throw new Error('Failed to update the branch');
      }

      const result = response;
      console.log('Branch updated successfully:', result);
      notifyTopRight('✅ Branch updated successfully!');
      
      setTimeout(() => {
        setIsSubmitting(false);
        navigateBackSafely();
      }, 2000);

    } catch (error) {
      console.error('Update error:', error);
      if (error.status === 409) {
        notifyError('Branch already exists with this country');
      } else {
        notifyError(error.response?.data?.error || 'Failed to update branch');
      }
      setIsSubmitting(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const res = await axiosInstance.get(`/auth/countrylist`);
        setCountries(res.data);
      } catch (err) {
        console.error("Failed to fetch countries:", err);
      }
    };
    fetchCountries();
  }, []);

  useEffect(() => {
    const fetchBranchData = async () => {
      try {
        // First try to get data from location state (passed from list)
        if (location.state?.branch) {
          const branch = location.state.branch;
          setBranchData(branch);
          setFormData({
            name: branch.name || "",
            location: branch.location || "",
            country: branch.country_id || ""
          });
          setDataLoaded(true);
        } else {
          // If no state data, fetch from API
          const res = await axiosInstance.get(`/settings/branches/${id}`);
          const branch = res.data;
          setBranchData(branch);
          setFormData({
            name: branch.name || "",
            location: branch.location || "",
            country: branch.country_id || ""
          });
          setDataLoaded(true);
        }
      } catch (err) {
        console.error("Failed to fetch branch data:", err);
        notifyError('Failed to load branch data');
      }
    };

    if (id) {
      fetchBranchData();
    }
  }, [id, location.state]);

  const notifyTopRight = (data) => {
    toast.success(`✔️ ${data}`, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: true,
    });
  };

  const notifyError = (data) => {
    toast.error(`❌ ${data}`, {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
    });
  };

  return (
    <div>
      <button
        className="back-button"
        onClick={() => {
          if (window.history.length > 2) {
            navigate(-1);
          } else {
            navigate('/settings/branchlist');
          }
        }}
      >
        <FiArrowLeft style={{ marginRight: '0.5rem' }} />
        Back
      </button>
      <div className="row" style={{ marginBottom: '0px !important' }}>
        <ToastContainer />
        <div className="col-xl-12 col-xxl-12">
          <div className="card">
            <div className="card-body">
              {!dataLoaded ? (
                <div className="text-center p-4">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Loading branch data...</p>
                </div>
              ) : (
                <section>
                  <div className="row">
                    <div className="col-lg-6 mb-2">
                      <div className="form-group mb-3">
                        <label className="text-label">Country*</label>
                        <select
                          name="country"
                          className="form-control"
                          value={formData.country}
                          onChange={handleChange}
                        >
                          <option value="">-- Select a country --</option>
                          {countries.map((country) => (
                            <option key={country.id} value={country.name}>
                              {country.name}
                            </option>
                          ))}
                        </select>
                        {errors.country && <div className="text-danger">{errors.country}</div>}
                      </div>
                    </div>

                    <div className="col-lg-6 mb-2">
                      <div className="form-group mb-3">
                        <label className="text-label">Branch Name*</label>
                        <input
                          type="text"
                          name="name"
                          className="form-control"
                          placeholder="Enter branch name"
                          maxLength={FIELD_LIMITS.BRANCH_NAME}
                          value={formData.name}
                          onChange={handleChange}
                        />
                        {errors.name && <div className="text-danger">{errors.name}</div>}
                      </div>
                    </div>

                    <div className="col-lg-12 mb-2">
                      <div className="form-group mb-3">
                        <label className="text-label">Location (Optional)</label>
                        <textarea
                          name="location"
                          className="form-control"
                          placeholder="Enter branch location"
                          rows="3"
                          value={formData.location}
                          onChange={handleChange}
                        />
                        {errors.location && <div className="text-danger">{errors.location}</div>}
                      </div>
                    </div>

                    <div className="text-end toolbar toolbar-bottom p-2">
                      <button 
                        type="button"
                        className="btn btn-cancel me-2" 
                        onClick={navigateBackSafely}
                        disabled={isSubmitting}
                      >
                        Cancel
                      </button>
                      <button 
                        className="btn user-form-btn sw-btn-next" 
                        onClick={handleSubmit} 
                        disabled={isSubmitting} 
                        style={{ maxWidth: '100px' }}
                      >
                        {loading ? (
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                        ) : null}
                        Submit
                      </button>
                    </div>
                  </div>
                </section>
              )}
            </div>
          </div>
        </div>
        <InternalFooter />
      </div>
    </div>
  );
};

export default EditBranch;